/**
 * Centralized media utilities.
 *
 * Conventions enforced (see /app/memory/MEDIA_AUDIT.md):
 *  - All cross-origin downloads MUST go through `forceDownload` (fetch → blob → anchor).
 *    The native `<a download>` attribute is silently ignored on cross-origin URLs.
 *  - Local previews (blob:/data:) can be turned into File objects with `blobToFile`.
 *  - When the source is already an HTTP(S) URL on R2, prefer passing the URL string
 *    to the backend directly instead of round-tripping through the browser.
 */

// ── Envío AP-6 · nombre de descarga con la marca de la plataforma ──────────
// «Studio Media Labs 4F7A2C.png»: el código sale del archivo (su clave en R2),
// así la misma imagen baja siempre con el mismo nombre, desde cualquier lugar
// (nodo, Galería, vista previa, lote). El archivo en R2 no cambia.
export const DOWNLOAD_PREFIX = 'Studio Media Labs';

const EXT_BY_MIME = {
  'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/gif': 'gif',
  'image/svg+xml': 'svg', 'video/mp4': 'mp4', 'video/webm': 'webm', 'video/quicktime': 'mov',
  'audio/mpeg': 'mp3', 'audio/mp3': 'mp3', 'audio/wav': 'wav', 'audio/x-wav': 'wav',
  'audio/wave': 'wav', 'audio/ogg': 'ogg', 'audio/mp4': 'm4a', 'audio/aac': 'aac',
  'application/pdf': 'pdf', 'application/json': 'json', 'text/plain': 'txt',
};

const extOf = (name) => {
  const m = /\.([a-z0-9]{2,5})$/i.exec(String(name || '').split(/[?#]/)[0]);
  return m ? m[1].toLowerCase() : '';
};

/** La parte estable de la URL: la clave del objeto (desde «user_…»), sin query. */
export function mediaKey(url) {
  const raw = String(url || '');
  let path = raw;
  try { path = new URL(raw).pathname; } catch (_e) { path = raw.split(/[?#]/)[0]; }
  try { path = decodeURIComponent(path); } catch (_e) { /* tal cual */ }
  const i = path.search(/(^|\/)user_/);
  return (i >= 0 ? path.slice(i).replace(/^\//, '') : path.replace(/^\/+/, '')) || raw;
}

/** Código de 6 caracteres (FNV-1a 32 bits) — mismo archivo, mismo código. */
export function fileCode(url) {
  const key = mediaKey(url);
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).toUpperCase().padStart(8, '0').slice(0, 6);
}

/** «Studio Media Labs 4F7A2C.png». La extensión sale del nombre sugerido,
 *  de la URL o del tipo del archivo. */
export function brandedFileName(url, suggested = '', mime = '') {
  const ext = extOf(suggested) || extOf(mediaKey(url)) || EXT_BY_MIME[String(mime).split(';')[0]] || '';
  return `${DOWNLOAD_PREFIX} ${fileCode(url)}${ext ? `.${ext}` : ''}`;
}

/**
 * Force-download a (potentially cross-origin) URL with a chosen filename.
 * Falls back to opening in a new tab if the fetch is blocked.
 * AP-6: el nombre final es siempre «Studio Media Labs <código>.<ext>»; el
 * `filename` de quien llama sólo aporta la extensión.
 */
export async function forceDownload(url, filename) {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = brandedFileName(url, filename, blob.type);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (error) {
    console.error('Download failed:', error);
    // Last-resort fallback so the user still gets the file.
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Fetch a same-origin blob:/data: URL and wrap it as a File.
 * Returns null on failure. Callers SHOULD avoid invoking this on http(s)
 * URLs from R2 — pass the URL string to the backend instead to bypass CORS.
 */
export async function blobToFile(url, filename, mimeFallback = 'image/jpeg') {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type || mimeFallback });
  } catch (error) {
    console.error('blobToFile failed:', error);
    return null;
  }
}
